import { z } from 'zod'

/**
 * Universal Exchange Rating (UER) contract types.
 *
 * The index scores each exchange on two 0–10 axes and multiplies them into a
 * 0–100 score: Accessibility A(e) × Execution E(e).
 */

export const UerAxisSchema = z.enum(['accessibility', 'execution'])
export type UerAxis = z.infer<typeof UerAxisSchema>

export const UerCategoryKeySchema = z.enum([
  'economics',
  'diligence',
  'listing_experience',
  'counterparty_transparency',
  'liquidity',
  'trade_activity',
  'listing_impact',
  'distribution',
])
export type UerCategoryKey = z.infer<typeof UerCategoryKeySchema>

export const UerCoverageSchema = z.enum([
  'measured',
  'unavailable',
  'insufficient_coverage',
  'not_applicable',
  'source_failure',
])
export type UerCoverage = z.infer<typeof UerCoverageSchema>

export const UerPeriodSchema = z.enum(['180d', '1y', 'all'])
export type UerPeriod = z.infer<typeof UerPeriodSchema>

export const UerVenueTypeSchema = z.enum(['cex', 'dex'])
export type UerVenueType = z.infer<typeof UerVenueTypeSchema>

export const UerMetricsStatusSchema = z.enum(['none', 'fresh', 'stale', 'expired'])
export type UerMetricsStatus = z.infer<typeof UerMetricsStatusSchema>

export const UerSnapshotSchema = z.object({
  snapshotId: z.string(),
  rubricVersion: z.number().int(),
  engineVersion: z.number().int(),
  effectiveDate: z.string().datetime(),
  computedAt: z.string().datetime(),
  metrics: z.object({
    status: UerMetricsStatusSchema,
    verifiedAt: z.string().nullable(),
    dataAsOf: z.string().nullable(),
  }),
})
export type UerSnapshot = z.infer<typeof UerSnapshotSchema>

export const UerScoreStatusSchema = z.enum(['complete', 'partial', 'withheld'])
export type UerScoreStatus = z.infer<typeof UerScoreStatusSchema>

export const UerWithheldReasonSchema = z.enum([
  'missingCriteria',
  'belowMinimumWeight',
  'noApplicableWeight',
  'axisWithheld',
])
export type UerWithheldReason = z.infer<typeof UerWithheldReasonSchema>

export const UerRatingBaseSchema = z.object({
  grade: z.string().nullable(),
  score: z.number().nullable(),
  percentile: z.number().nullable(),
  coverage: UerCoverageSchema,
})
export type UerRatingBase = z.infer<typeof UerRatingBaseSchema>

export const UerOverallRatingSchema = UerRatingBaseSchema.extend({
  status: UerScoreStatusSchema,
  withheldReason: UerWithheldReasonSchema.nullable(),
})
export type UerOverallRating = z.infer<typeof UerOverallRatingSchema>

export const UerRatingSchema = UerOverallRatingSchema.extend({
  scoredWeightShare: z.number().nullable(),
})
export type UerRating = z.infer<typeof UerRatingSchema>

export const UerExchangeRefSchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  logoUrl: z.string().nullable(),
  venueType: UerVenueTypeSchema,
  tier: z.number().int().positive().nullable(),
})
export type UerExchangeRef = z.infer<typeof UerExchangeRefSchema>

export const UerCategoryScoreSchema = UerRatingSchema.extend({
  key: UerCategoryKeySchema,
  axis: UerAxisSchema,
  label: z.string(),
})
export type UerCategoryScore = z.infer<typeof UerCategoryScoreSchema>

export const UerRequirementKeySchema = z.enum(['fees_cash', 'fees_tokens', 'security_deposit', 'liquidity_capital'])
export type UerRequirementKey = z.infer<typeof UerRequirementKeySchema>

export const UerLevelSchema = z.enum(['low', 'medium', 'high'])
export type UerLevel = z.infer<typeof UerLevelSchema>

export const UerEconomicsSummarySchema = z.object({
  costBand: z.object({ from: z.string(), to: z.string() }).nullable(),
  requirements: z.array(
    z.object({
      key: UerRequirementKeySchema,
      label: z.string(),
      required: z.boolean().nullable(),
      level: UerLevelSchema.nullable(),
    }),
  ),
})
export type UerEconomicsSummary = z.infer<typeof UerEconomicsSummarySchema>

export const UerLeaderboardExchangeSchema = UerExchangeRefSchema.extend({
  rank: z.number().int().nullable(),
  grade: z.string().nullable(),
  overallScore: z.number().nullable(),
  overall: UerOverallRatingSchema,
  momChangePct: z.number().nullable(),
  // Quant may add reasons (e.g. measurementChanged), so any value is accepted
  changeSuppressed: z.string().nullable(),
  accessibility: UerRatingSchema,
  execution: UerRatingSchema,
  listingsCount: z.number().int().nullable(),
  medianListingRoiPct: z.number().nullable(),
  medianAdvUsd: z.number().nullable(),
  medianDepthUsd: z.number().nullable(),
  commentary: z.string().nullable(),
  economics: UerEconomicsSummarySchema,
  categories: z.array(UerCategoryScoreSchema),
})
export type UerLeaderboardExchange = z.infer<typeof UerLeaderboardExchangeSchema>

export const UerLeaderboardResponseSchema = UerSnapshotSchema.extend({
  period: UerPeriodSchema,
  periodApplied: z.string(),
  comparisonDate: z.string(),
  previousSnapshotId: z.string().nullable(),
  ratedCount: z.number().int(),
  totalCount: z.number().int(),
  highlights: z.object({
    bestOverall: z.string().nullable(),
    cheapestBestExecution: z.string().nullable(),
    priciestWorstExecution: z.string().nullable(),
  }),
  exchanges: z.array(UerLeaderboardExchangeSchema),
})
export type UerLeaderboardResponse = z.infer<typeof UerLeaderboardResponseSchema>

export const UerSnapshotRefSchema = z.object({
  snapshotId: z.string(),
  effectiveDate: z.string().datetime(),
  computedAt: z.string().datetime(),
  rubricVersion: z.number().int(),
  engineVersion: z.number().int(),
})
export type UerSnapshotRef = z.infer<typeof UerSnapshotRefSchema>

export const UerMoverSchema = UerExchangeRefSchema.extend({
  overallScore: z.number(),
  previousScore: z.number(),
  overallChange: z.number(),
  momChangePct: z.number().nullable(),
})
export type UerMover = z.infer<typeof UerMoverSchema>

export const UerTopMoversResponseSchema = UerSnapshotSchema.extend({
  period: UerPeriodSchema,
  periodApplied: z.string(),
  comparisonDate: z.string(),
  current: UerSnapshotRefSchema,
  previous: UerSnapshotRefSchema.nullable(),
  comparable: z.boolean(),
  gainers: z.array(UerMoverSchema),
  losers: z.array(UerMoverSchema),
})
export type UerTopMoversResponse = z.infer<typeof UerTopMoversResponseSchema>

export const UerUnitSchema = z.enum(['usd', 'usdPerDay', 'days', 'bps', 'percent', 'ratio', 'count'])
export type UerUnit = z.infer<typeof UerUnitSchema>

export const UerUniverseSchema = z.enum(['basket', 'listingImpact'])
export type UerUniverse = z.infer<typeof UerUniverseSchema>

export const UerMeasurementPeriodSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('trailing'), from: z.string(), to: z.string() }),
  z.object({
    kind: z.literal('listingRelative'),
    day: z.number().int(),
    convention: z.enum(['calendarListingDayIsDay1', 'calendarListingDayIsDay0', 'elapsedSinceListingInstant']),
  }),
  z.object({ kind: z.literal('pointInTime'), at: z.string() }),
])
export type UerMeasurementPeriod = z.infer<typeof UerMeasurementPeriodSchema>

export const UerMeasurementSchema = z.object({
  universe: UerUniverseSchema,
  period: UerMeasurementPeriodSchema,
})
export type UerMeasurement = z.infer<typeof UerMeasurementSchema>

export const UerCriterionSchema = UerRatingBaseSchema.extend({
  key: z.string(),
  label: z.string(),
  value: z.number().nullable(),
  unit: UerUnitSchema.nullable(),
  commentary: z.string().nullable(),
  measurement: UerMeasurementSchema.nullable(),
  summaryLabel: z.string().nullish(),
})
export type UerCriterion = z.infer<typeof UerCriterionSchema>

export const UerCategoryDetailSchema = UerCategoryScoreSchema.extend({
  commentary: z.string().nullable(),
  criteria: z.array(UerCriterionSchema),
})
export type UerCategoryDetail = z.infer<typeof UerCategoryDetailSchema>

export const UerExchangeResponseSchema = UerSnapshotSchema.extend({
  exchange: UerExchangeRefSchema,
  rank: z.number().int().nullable(),
  totalRanked: z.number().int(),
  grade: z.string().nullable(),
  overallScore: z.number().nullable(),
  overall: UerOverallRatingSchema,
  accessibility: UerRatingSchema,
  execution: UerRatingSchema,
  commentary: z.string().nullable(),
  categories: z.array(UerCategoryDetailSchema),
  economics: UerEconomicsSummarySchema,
  openingMechanism: z.object({ commentary: z.string().nullable() }),
})
export type UerExchangeResponse = z.infer<typeof UerExchangeResponseSchema>

export const UerSeriesKeySchema = z.enum([
  'depth_25bps',
  'depth_200bps',
  'bid_ask_spread',
  'buy_sell_ratio',
  'buy_side_fill_volume',
  'total_fill_volume',
  'price_change',
  'avg_fdv',
  'avg_mc',
  'active_user_reach',
  // Fixture-only: Quant scores both as one `institutional_presence` criterion
  'mm_depth_2',
  'mm_fill_volume',
])
export type UerSeriesKey = z.infer<typeof UerSeriesKeySchema>

export const UerHorizonSchema = z.object({
  key: z.string(),
  label: z.string(),
})
export type UerHorizon = z.infer<typeof UerHorizonSchema>

export const UerSeriesSchema = z.object({
  status: UerCoverageSchema,
  unit: UerUnitSchema,
  points: z.array(
    z.object({
      horizon: z.string(),
      value: z.number().nullable(),
      parts: z.record(z.string(), z.number()).nullish(),
    }),
  ),
})
export type UerSeries = z.infer<typeof UerSeriesSchema>

export const UerRegionSchema = z.enum(['NA', 'SA', 'EU', 'ME', 'AF', 'AS', 'OC'])
export type UerRegion = z.infer<typeof UerRegionSchema>

export const UerProductSchema = z.enum(['spot', 'perps', 'options', 'earn', 'lend'])
export type UerProduct = z.infer<typeof UerProductSchema>

export const UerExchangeSeriesResponseSchema = z.object({
  snapshotId: z.string(),
  computedAt: z.string().datetime(),
  status: z.enum(['ok', 'partial', 'failed']),
  horizons: z.array(UerHorizonSchema),
  metrics: z.record(UerSeriesKeySchema, UerSeriesSchema),
  geography: z.object({
    status: UerCoverageSchema,
    regions: z.array(z.object({ region: UerRegionSchema, share: z.number() })),
    topCountry: z.object({ code: z.string(), name: z.string() }).nullable(),
  }),
  productCoverage: z.object({
    status: UerCoverageSchema,
    products: z.array(z.object({ key: UerProductSchema, label: z.string(), available: z.boolean() })),
  }),
})
export type UerExchangeSeriesResponse = z.infer<typeof UerExchangeSeriesResponseSchema>

export const UerExchangesResponseSchema = UerSnapshotSchema.extend({
  exchanges: z.array(UerExchangeRefSchema),
})
export type UerExchangesResponse = z.infer<typeof UerExchangesResponseSchema>
